import firebase from '@/firebase'
import firestoreDebugger from '@/utils/firestoreDebugger'

const db = firestoreDebugger.isDebugEnabled() ? firestoreDebugger.db : firebase.db

const state = {
  customModels: [],
  mmmModels: [],
  allModels: [],
  ekrmModels: [],
  ekrmModelsObj: {},
  referenceModelsArray: [],
  referenceModels: {},
  managedUsers: [],
  userList: [],
  groups: [],
  auditLogs: [],
  auditLoading: false,
  auditTotal: 0,
  // Unsubscribe functions for Firestore listeners to prevent memory leaks
  unsubscribeGroups: null,
  unsubscribeManagedUsers: null,
  unsubscribeEkrmModels: null,
  unsubscribeCustomModels: null,
  unsubscribeMmmModels: null,
  unsubscribeAllModels: null,
  deletedModels: [],
  unsubscribeDeletedModels: null
}

const getters = {
  customModels: state => {
    return state.customModels
  },
  mmmModels: state => {
    return state.mmmModels
  },
  deletedModels: state => {
    return state.deletedModels
  },
  allModels: state => {
    return state.allModels
  },
  ekrmModels: state => {
    return state.ekrmModels
  },
  ekrmModelsObj: state => {
    return state.ekrmModelsObj
  },
  referenceModelsArray: state => {
    return state.referenceModelsArray
  },
  referenceModels: state => {
    return state.referenceModels
  },
  managedUsers: state => {
    return state.managedUsers
  },
  userList: state => {
    return state.userList
  },
  groups: state => {
    return state.groups
  },
  auditLogs: state => {
    return state.auditLogs
  },
  auditLoading: state => {
    return state.auditLoading
  },
  auditTotal: state => {
    return state.auditTotal
  }
}

const actions = {
  // Cleanup all Firestore listeners to prevent memory leaks
  cleanupListeners (context) {
    if (context.state.unsubscribeGroups) {
      context.state.unsubscribeGroups()
      context.commit('setUnsubscribeGroups', null)
    }
    if (context.state.unsubscribeManagedUsers) {
      context.state.unsubscribeManagedUsers()
      context.commit('setUnsubscribeManagedUsers', null)
    }
    if (context.state.unsubscribeEkrmModels) {
      context.state.unsubscribeEkrmModels()
      context.commit('setUnsubscribeEkrmModels', null)
    }
    if (context.state.unsubscribeCustomModels) {
      context.state.unsubscribeCustomModels()
      context.commit('setUnsubscribeCustomModels', null)
    }
    if (context.state.unsubscribeMmmModels) {
      context.state.unsubscribeMmmModels()
      context.commit('setUnsubscribeMmmModels', null)
    }
    if (context.state.unsubscribeAllModels) {
      context.state.unsubscribeAllModels()
      context.commit('setUnsubscribeAllModels', null)
    }
    context.dispatch('stopDeletedModels')
  },
  // Cleanup only GroupManager's listeners (groups, customModels, mmmModels, ekrmModels)
  // Used by GroupManager's beforeDestroy to prevent listener leaks on tab switches
  cleanupGroupManagerListeners (context) {
    if (context.state.unsubscribeGroups) {
      context.state.unsubscribeGroups()
      context.commit('setUnsubscribeGroups', null)
    }
    if (context.state.unsubscribeCustomModels) {
      context.state.unsubscribeCustomModels()
      context.commit('setUnsubscribeCustomModels', null)
    }
    if (context.state.unsubscribeMmmModels) {
      context.state.unsubscribeMmmModels()
      context.commit('setUnsubscribeMmmModels', null)
    }
    if (context.state.unsubscribeEkrmModels) {
      context.state.unsubscribeEkrmModels()
      context.commit('setUnsubscribeEkrmModels', null)
    }
  },
  updateModelStages (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(payload.client).update({
        stages: payload.stages
      }).then(() => {
        resolve()
      }).catch(function (error) {
        console.error('Error updating model stages: ', error)
        reject(error)
      })
    })
  },
  saveModelSettings (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(payload.client).update(payload.json).then(() => {
        resolve()
      }).catch(function (error) {
        console.error('Error saving model settings: ', error)
        reject(error)
      })
    })
  },
  // Loads the client_metadata_categories registry: one doc per metadata field
  // ({ values: [...] }). Returns a plain object { <fieldKey>: string[] }.
  loadClientMetadataCategories (context) {
    return new Promise((resolve, reject) => {
      db.collection('client_metadata_categories').get().then(snapshot => {
        const categories = {}
        snapshot.forEach(doc => {
          const data = doc.data()
          categories[doc.id] = Array.isArray(data.values) ? data.values : []
        })
        resolve(categories)
      }).catch(function (error) {
        console.error('Error loading client metadata categories: ', error)
        reject(error)
      })
    })
  },
  // Adds new values to the client_metadata_categories registry. payload.valuesByField
  // is { <fieldKey>: string[] }. arrayUnion (with merge:true) creates the field doc
  // if missing and de-duplicates, preserving values registered by the import script.
  addClientMetadataCategoryValues (context, payload) {
    return new Promise((resolve, reject) => {
      const valuesByField = payload.valuesByField || {}
      const batch = db.batch()
      Object.keys(valuesByField).forEach(field => {
        const values = valuesByField[field]
        if (values && values.length > 0) {
          const ref = db.collection('client_metadata_categories').doc(field)
          batch.set(ref, {
            values: firebase.firestore.FieldValue.arrayUnion(...values)
          }, { merge: true })
        }
      })
      batch.commit().then(() => {
        // Audit each newly created value (every value reaching this action is new,
        // since the caller only passes values not already in the registry). Best-effort.
        if (payload.uid) {
          Object.keys(valuesByField).forEach(field => {
            (valuesByField[field] || []).forEach(value => {
              firebase.log(payload.uid, 'createClientMetadataCategoryValue', {
                field: field,
                value: value,
                clientId: payload.clientId || null,
                performedByEmail: payload.performedByEmail || null
              })
            })
          })
        }
        resolve()
      }).catch(function (error) {
        console.error('Error adding client metadata category values: ', error)
        reject(error)
      })
    })
  },
  // Loads the channel_taxonomy_levels registry: one doc per taxonomy level.
  // Each value is a { name, abbreviation } map. Independent levels store
  // { values: [...] }; levels flagged with depends_on_previous: true store
  // { values_by_parent: { <parentName>: [...] } } where the parent is the
  // previous level's value name. Returns
  // { values: { <level>: object[] | { <parent>: object[] } },
  //   dependsOnPrevious: { <level>: boolean } }.
  loadChannelTaxonomyLevels (context) {
    return new Promise((resolve, reject) => {
      // Entries without a non-empty name are unusable in the UI and dropped.
      const cleanValues = list => list.filter(value =>
        value && typeof value === 'object' && String(value.name || '').trim().length > 0)
      db.collection('channel_taxonomy_levels').get().then(snapshot => {
        const values = {}
        const dependsOnPrevious = {}
        snapshot.forEach(doc => {
          const data = doc.data()
          // L0 has no previous level and L6 is free text, so a stray flag on
          // either is ignored and the doc is read as independent.
          const dependent = data.depends_on_previous === true && doc.id !== 'L0' && doc.id !== 'L6'
          dependsOnPrevious[doc.id] = dependent
          if (dependent) {
            const byParent = {}
            const raw = data.values_by_parent
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
              Object.keys(raw).forEach(parent => {
                if (Array.isArray(raw[parent])) {
                  byParent[parent] = cleanValues(raw[parent])
                }
              })
            }
            values[doc.id] = byParent
          } else {
            values[doc.id] = Array.isArray(data.values) ? cleanValues(data.values) : []
          }
        })
        resolve({ values: values, dependsOnPrevious: dependsOnPrevious })
      }).catch(function (error) {
        console.error('Error loading channel taxonomy levels: ', error)
        reject(error)
      })
    })
  },
  // Adds new values to the channel_taxonomy_levels registry. Each value is a
  // { name, abbreviation } map. payload.valuesByLevel is { <level>: object[] }
  // for independent levels and { <level>: { <parent>: object[] } } for
  // dependent ones. arrayUnion (with merge:true) creates the level doc if
  // missing and de-duplicates (maps compare by deep equality). Dependent
  // values are written as a literal nested map (set + merge), never a
  // dot-notation field path: parent names may contain dots, which the SDK
  // would split into nested keys. depends_on_previous is never written here —
  // it is per-instance configuration managed directly in Firestore.
  addChannelTaxonomyLevelValues (context, payload) {
    return new Promise((resolve, reject) => {
      const valuesByLevel = payload.valuesByLevel || {}
      const batch = db.batch()
      Object.keys(valuesByLevel).forEach(level => {
        const values = valuesByLevel[level]
        const ref = db.collection('channel_taxonomy_levels').doc(level)
        if (Array.isArray(values)) {
          if (values.length > 0) {
            batch.set(ref, {
              values: firebase.firestore.FieldValue.arrayUnion(...values)
            }, { merge: true })
          }
        } else if (values && typeof values === 'object') {
          const nested = {}
          Object.keys(values).forEach(parent => {
            if (Array.isArray(values[parent]) && values[parent].length > 0) {
              nested[parent] = firebase.firestore.FieldValue.arrayUnion(...values[parent])
            }
          })
          if (Object.keys(nested).length > 0) {
            batch.set(ref, { values_by_parent: nested }, { merge: true })
          }
        }
      })
      batch.commit().then(() => {
        // Audit each newly created value (every value reaching this action is new,
        // since the caller only passes values not already in the registry). Best-effort.
        if (payload.uid) {
          Object.keys(valuesByLevel).forEach(level => {
            const values = valuesByLevel[level]
            const logValue = (value, parent) => {
              firebase.log(payload.uid, 'createChannelTaxonomyLevelValue', {
                level: level,
                value: value.name,
                abbreviation: value.abbreviation || null,
                parent: parent,
                clientId: payload.clientId || null,
                performedByEmail: payload.performedByEmail || null
              })
            }
            if (Array.isArray(values)) {
              values.forEach(value => logValue(value, null))
            } else if (values && typeof values === 'object') {
              Object.keys(values).forEach(parent => {
                (values[parent] || []).forEach(value => logValue(value, parent))
              })
            }
          })
        }
        resolve()
      }).catch(function (error) {
        console.error('Error adding channel taxonomy level values: ', error)
        reject(error)
      })
    })
  },
  loadGroups (context, payload) {
    return new Promise((resolve, reject) => {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeGroups) {
        context.state.unsubscribeGroups()
      }
      const unsubscribe = db.collection('groups').doc('data').collection('groups').onSnapshot(function (snapshot) {
        var groups = []
        snapshot.forEach(doc => {
          const data = doc.data()
          groups.push({
            id: doc.id,
            name: data.name,
            dateCreated: data.date_created,
            createdBy: data.created_by
          })
        })
        context.commit('setGroups', {
          groups: groups
        })
        resolve()
      })
      context.commit('setUnsubscribeGroups', unsubscribe)
    })
  },
  loadModelUserAccess (context, payload) {
    return new Promise((resolve, reject) => {
      const clientId = payload.clientId
      db.collection('permissions').doc('client_edit_access').collection('clients').doc(clientId).collection('users').get().then(function (snapshot) {
        let users = []
        snapshot.forEach(doc => {
          users.push(doc.id)
        })
        resolve(users)
      })
    })
  },
  batchSaveModelUserAccess (context, payload) {
    return new Promise((resolve, reject) => {
      const userChanges = payload.userChanges
      // Call the secure Cloud Function for batch operations
      const batchSaveModelUserAccessWithAudit = firebase.functions.httpsCallable('permissions-batchSaveModelUserAccessWithAudit')
      batchSaveModelUserAccessWithAudit({
        userChanges: userChanges,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Batch permissions updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating batch permissions:', error)
        reject(error)
      })
    })
  },
  batchSaveReferenceUserAccess (context, payload) {
    return new Promise((resolve, reject) => {
      const { userChanges, referenceType = 'references' } = payload
      // Call the secure Cloud Function for batch operations
      const batchSaveReferenceUserAccessWithAudit = firebase.functions.httpsCallable('permissions-batchSaveReferenceUserAccessWithAudit')
      batchSaveReferenceUserAccessWithAudit({
        userChanges: userChanges,
        referenceType: referenceType,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Batch reference permissions updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating batch reference permissions:', error)
        reject(error)
      })
    })
  },
  loadUserList (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('users').where('isAdmin', '==', false).where('isManager', '==', false).where('isScientist', '==', false).get().then(function (users) {
        resolve()
        context.commit('setUserList', {
          users: users.docs.map(doc => ({ ...doc.data(), id: doc.id }))
        })
      })
    })
  },
  loadManagedUsers (context, payload) {
    return new Promise(async (resolve, reject) => {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeManagedUsers) {
        context.state.unsubscribeManagedUsers()
      }

      const startTime = performance.now()

      try {
        // BUGFIX: Use .get() instead of .onSnapshot() for initial load
        // onSnapshot was not firing reliably without debug mode, but .get() works consistently
        const snapshot = await db.collection('users')
          .where('isAdmin', '==', false)
          .where('isManager', '==', false)
          .where('isScientist', '==', false)
          .get()

        const initialLoadTime = (performance.now() - startTime).toFixed(0)
        console.log(`Loaded ${snapshot.size} users in ${initialLoadTime}ms, fetching permissions...`)

        // First, commit basic user data immediately so UI can render
        const basicUsers = snapshot.docs.map(doc => ({
          ...doc.data(),
          // Expose `name` (alias of email) from the store so consumers never
          // mutate the frozen user objects in place to set it.
          name: doc.data().email,
          clients: [],
          references: [],
          ekrms: [],
          id: doc.id
        }))

        context.commit('setManagedUsers', {
          users: basicUsers
        })

        // Resolve immediately so page loads fast (permissions load in background)
        resolve()

        // Now fetch permissions asynchronously in the background
        const permissionStartTime = performance.now()
        const userPromises = []

        snapshot.forEach(doc => {
          const userPromise = Promise.all([
            db.collection('permissions').doc('client_edit_access').collection('users').doc(doc.id).collection('clients').get().catch(err => {
              console.error('Error loading clients for user:', doc.id, err)
              return { docs: [] }
            }),
            db.collection('users').doc(doc.id).collection('metadata').doc('references').get().catch(err => {
              console.error('Error loading references for user:', doc.id, err)
              return { data: () => null }
            }),
            db.collection('users').doc(doc.id).collection('metadata').doc('ekrms').get().catch(err => {
              console.error('Error loading ekrms for user:', doc.id, err)
              return { data: () => null }
            })
          ]).then(([itemClients, itemReferences, itemEkrms]) => {
            const clients = itemClients.docs.map(doc => doc.id)
            let references = []
            if (typeof itemReferences.data === 'function' && itemReferences.data() && itemReferences.data().references) {
              references = itemReferences.data().references
            }
            let ekrms = []
            if (typeof itemEkrms.data === 'function' && itemEkrms.data() && itemEkrms.data().references) {
              ekrms = itemEkrms.data().references
            }

            return {
              ...doc.data(),
              name: doc.data().email,
              clients: clients,
              references: references,
              ekrms: ekrms,
              id: doc.id
            }
          }).catch(err => {
            // If individual user processing fails, return user with empty arrays
            console.error('Error processing user data:', doc.id, err)
            return {
              ...doc.data(),
              name: doc.data().email,
              clients: [],
              references: [],
              ekrms: [],
              id: doc.id
            }
          })

          userPromises.push(userPromise)
        })

        const users = await Promise.all(userPromises)
        context.commit('setManagedUsers', {
          users: users
        })

        const permissionLoadTime = (performance.now() - permissionStartTime).toFixed(0)
        console.log(`Loaded permissions for ${users.length} users in ${permissionLoadTime}ms`)

        // NOTE: No real-time onSnapshot listener here (on-demand load).
        // Previously a listener on the users collection re-read every user's
        // permission sub-docs on any change. Saving group users writes a `groups`
        // field to each affected user doc (setGroupUsersWithAudit), which fired
        // that listener and triggered thousands of reads + a full reactive
        // re-commit in one burst — the cause of the Groups-tab browser crashes.
        // The list is refreshed on view mount (loadManagedUsers is called from
        // manageUserModels.vue's created hook), which is sufficient here since
        // group membership is not displayed in any table.
      } catch (error) {
        console.error('Error in loadManagedUsers:', error)
        reject(error)
      }
    })
  },
  loadEkrmModels (context) {
    return new Promise(function (resolve) {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeEkrmModels) {
        context.state.unsubscribeEkrmModels()
      }
      var resolved = false
      var unsubscribe = db.collection('ekrm').onSnapshot(function (snapshot) {
        var ekrmModels = []
        var ekrmModelsObj = {}
        snapshot.forEach(function (doc) {
          var data = doc.data()
          if (data.config) {
            ekrmModelsObj[doc.id] = data
          }
          ekrmModels.push({
            ...data,
            id: doc.id
          })
        })
        context.commit('setEkrmModels', {
          ekrmModels: ekrmModels
        })
        context.commit('setEkrmModelsObj', {
          ekrmModelsObj: ekrmModelsObj
        })
        if (!resolved) {
          resolved = true
          resolve()
        }
      })
      context.commit('setUnsubscribeEkrmModels', unsubscribe)
    })
  },
  loadReferenceModels (context) {
    return new Promise((resolve, reject) => {
      let referenceModels = {}
      let referenceModelsArray = []
      db.collection('reference_models').get().then(function (snapshot) {
        snapshot.forEach(doc => {
          const data = doc.data()
          referenceModels[doc.id] = data
          referenceModelsArray.push({
            ...data,
            id: doc.id
          })
        })
        context.commit('setReferenceModels', {
          referenceModels: referenceModels
        })
        context.commit('setReferenceModelsArray', {
          referenceModelsArray: referenceModelsArray
        })
        resolve()
      })
    })
  },
  loadCustomModels (context) {
    return new Promise((resolve, reject) => {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeCustomModels) {
        context.state.unsubscribeCustomModels()
      }
      const unsubscribe = db.collection('clients').where('type', '!=', 'mmm').onSnapshot(function (snapshot) {
        var customModels = []
        snapshot.forEach(doc => {
          let data = doc.data()
          // MEMORY OPTIMIZATION: Keep model_report as string to avoid Vue reactivity overhead
          // Only parse when actually needed (e.g., in ModelReport view)
          // This reduces memory usage from ~1GB to ~35MB
          // Note: If you need the parsed report, parse it in the component, not in the store
          customModels.push({
            ...data,
            id: doc.id
          })
        })
        context.commit('setCustomModels', {
          customModels: customModels
        })
        resolve()
      })
      context.commit('setUnsubscribeCustomModels', unsubscribe)
    })
  },
  loadMmmModels (context) {
    return new Promise(function (resolve) {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeMmmModels) {
        context.state.unsubscribeMmmModels()
      }
      var resolved = false
      var unsubscribe = db.collection('clients').where('type', '==', 'mmm').onSnapshot(function (snapshot) {
        var models = []
        snapshot.forEach(function (doc) {
          models.push({
            ...doc.data(),
            id: doc.id
          })
        })
        context.commit('setMmmModels', {
          mmmModels: models
        })
        if (!resolved) {
          resolved = true
          resolve()
        }
      })
      context.commit('setUnsubscribeMmmModels', unsubscribe)
    })
  },
  loadAllModels (context) {
    return new Promise((resolve, reject) => {
      // Cleanup existing listener before creating a new one
      if (context.state.unsubscribeAllModels) {
        context.state.unsubscribeAllModels()
      }
      const unsubscribe = db.collection('clients').orderBy('name').onSnapshot(function (snapshot) {
        var allModels = []
        snapshot.forEach(doc => {
          // MEMORY: the only consumer of this getter is the model picker
          // (MultiSelect, item-text="name" item-value="id"), so store just id + name.
          // Previously we spread the full client doc — including the large
          // model_report string — for every client, ballooning memory with
          // thousands of models and contributing to the manage-users crashes.
          const data = doc.data()
          allModels.push({
            id: doc.id,
            name: data.name
          })
        })
        context.commit('setAllModels', {
          allModels: allModels
        })
        resolve()
      }, (error) => {
        // Handle snapshot errors
        console.error('Error in clients snapshot listener:', error)
        reject(error)
      })
      context.commit('setUnsubscribeAllModels', unsubscribe)
    })
  },
  createGroup (context, payload) {
    return new Promise((resolve, reject) => {
      // Call the secure Cloud Function for group creation with audit trail
      const createGroupWithAudit = firebase.functions.httpsCallable('permissions-createGroupWithAudit')
      createGroupWithAudit({
        name: payload.name,
        userAgent: payload.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
      }).then((result) => {
        console.log('Group created with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error creating group:', error)
        reject(error)
      })
    })
  },
  deleteGroup (context, payload) {
    return new Promise((resolve, reject) => {
      // Call the secure Cloud Function for group deletion with audit trail
      const deleteGroupWithAudit = firebase.functions.httpsCallable('permissions-deleteGroupWithAudit')
      deleteGroupWithAudit({
        groupId: payload.id,
        userAgent: payload.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
      }).then((result) => {
        console.log('Group deleted with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error deleting group:', error)
        reject(error)
      })
    })
  },
  getGroupData (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('groups').doc('data').collection('groups').doc(payload.id).get().then(snapshot => {
        const data = snapshot.data()
        resolve({
          users: data.users,
          models: data.models,
          references: data.references,
          ekrms: data.ekrms
        })
      }).catch(error => {
        reject(error)
      })
    })
  },
  setGroupUsers (context, payload) {
    return new Promise((resolve, reject) => {
      const { groupId, selectedUsers } = payload
      // Call the secure Cloud Function for group user management with audit trail
      const setGroupUsersWithAudit = firebase.functions.httpsCallable('permissions-setGroupUsersWithAudit')
      setGroupUsersWithAudit({
        groupId: groupId,
        selectedUsers: selectedUsers,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Group users updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating group users:', error)
        reject(error)
      })
    })
  },
  setGroupModels (context, payload) {
    return new Promise((resolve, reject) => {
      const { groupId, selectedModels } = payload
      // Call the secure Cloud Function for group model management with audit trail
      const setGroupModelsWithAudit = firebase.functions.httpsCallable('permissions-setGroupModelsWithAudit')
      setGroupModelsWithAudit({
        groupId: groupId,
        selectedModels: selectedModels,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Group models updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating group models:', error)
        reject(error)
      })
    })
  },
  setGroupReferences (context, payload) {
    return new Promise((resolve, reject) => {
      const { groupId, selectedReferences } = payload
      // Call the secure Cloud Function for group reference management with audit trail
      const setGroupReferencesWithAudit = firebase.functions.httpsCallable('permissions-setGroupReferencesWithAudit')
      setGroupReferencesWithAudit({
        groupId: groupId,
        selectedReferences: selectedReferences,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Group references updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating group references:', error)
        reject(error)
      })
    })
  },
  setGroupEkrms (context, payload) {
    return new Promise((resolve, reject) => {
      const { groupId, selectedEkrms } = payload
      // Call the secure Cloud Function for group EKRM management with audit trail
      const setGroupEkrmsWithAudit = firebase.functions.httpsCallable('permissions-setGroupEkrmsWithAudit')
      setGroupEkrmsWithAudit({
        groupId: groupId,
        selectedEkrms: selectedEkrms,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Group EKRMs updated with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error updating group EKRMs:', error)
        reject(error)
      })
    })
  },
  createEkrm (context, payload) {
    return new Promise((resolve, reject) => {
      const ekrm = {
        definition: payload.definition,
        originalDefinition: payload.originalDefinition,
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: payload.createdBy,
        created_by_id: payload.createdById
      }
      // Which wizard made it. Only the Model Composer passes one: its EKRM is a step towards the
      // custom model ekrmBuilt creates, and is hidden wherever EKRMs are offered (see ekrmOrigin.js).
      if (payload.origin) {
        ekrm.origin = payload.origin
      }
      db.collection('ekrm').add(ekrm).then(docRef => {
        console.log('EKRM created with ID: ', docRef.id)
        resolve(docRef.id)
      }).catch(error => {
        console.error('Error creating EKRM: ', error)
        reject(error)
      })
    })
  },
  createCustomModel (context, payload) {
    return new Promise((resolve, reject) => {
      let batch = db.batch()
      let newClient = db.collection('clients').doc()
      batch.set(newClient, {
        ...payload.model,
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: payload.user,
        created_by_name: payload.userName || payload.user
      })
      if (payload.model.type === 'ekrm') {
        batch.set(db.collection('stats').doc('clients').collection('created').doc(), {
          ...payload.model,
          date_created: firebase.firestore.FieldValue.serverTimestamp(),
          created_by: payload.user,
          created_by_name: payload.userName || payload.user
        })
      }
      batch.commit().then(() => {
        console.log('Custom model created with ID: ', newClient.id)
        resolve(newClient.id)
      }).catch(error => {
        console.error('Error creating custom model: ', error)
        reject(error)
      })
      /* db.collection('clients').add({
        ...payload.model,
        date_created: firebase.firestore.FieldValue.serverTimestamp(),
        created_by: payload.user
      }).then(docRef => {
        console.log('Custom model created with ID: ', docRef.id)
        resolve(docRef.id)
      }).catch(error => {
        console.error('Error creating custom model: ', error)
        reject(error)
      }) */
    })
  },
  editCustomModel (context, payload) {
    return new Promise((resolve, reject) => {
      // Create new model. optimizer_gpu is left behind: only admins may set it (firestore.rules
      // would refuse the copy) and it belongs to Ad-Hoc models, not custom ones.
      const model = { ...payload.model }
      delete model.optimizer_gpu
      db.collection('clients').add({
        ...model,
        // Preserve the model's original type (e.g. 'ekrm'); fall back to 'custom'
        type: model.type === 'ekrm' ? 'ekrm' : 'custom',
        date_created: payload.date_created,
        created_by: payload.created_by,
        created_by_name: payload.created_by_name || payload.created_by,
        date_edited: firebase.firestore.FieldValue.serverTimestamp(),
        edited_by: payload.user,
        edited_by_name: payload.userName || payload.user
      }).then(docRef => {
        console.log('Custom model edited with new ID: ', docRef.id)
        // Delete old model
        db.collection('clients').doc(payload.client).delete().then(function () {
          console.log('Old client successfully deleted: ', payload.client)
          resolve(docRef.id)
        }).catch(error => {
          console.error('Error deleting old custom model: ', error)
          reject(error)
        })
      }).catch(function (error) {
        console.error('Error creating new custom model: ', error)
        reject(error)
      })
    })
  },
  deleteEkrmModel (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('ekrm').doc(payload.ekrm_id).delete().then(function () {
        console.log('EKRM deleted')
        resolve()
      }).catch(function (error) {
        console.error('Error deleting EKRM', error)
        reject(error)
      })
    })
  },
  // An EKRM that is already built goes to the trash, where it can be restored until it is purged.
  // deleteEkrmModel above deletes for good, which is what one still being built gets.
  trashEkrmModel (context, payload) {
    const trashEkrm = firebase.functions.httpsCallable('modelTrash-trashEkrm')
    return trashEkrm({ ekrmId: payload.ekrm_id }).then(result => result.data)
  },
  // Deleting moves the model to the trash, where an admin, a scientist or a manager can restore it
  // until it is purged. Resolves with { trashed, failed }, and rejects when nothing could be deleted.
  // payload: { client, source, includeEkrm (Model Composer: the EKRM goes with its model) }
  deleteCustomModel (context, payload) {
    return context.dispatch('deleteCustomModelsBulk', {
      selectedModels: [{ id: payload.client }],
      source: payload.source,
      includeEkrm: payload.includeEkrm
    })
  },
  deleteCustomModelsBulk (context, payload) {
    const trashModels = firebase.functions.httpsCallable('modelTrash-trashModels')
    return trashModels({
      clientIds: payload.selectedModels.map(model => model.id),
      source: payload.source || 'modelManager',
      includeEkrm: payload.includeEkrm === true
    }).then(result => {
      if (result.data.trashed.length === 0) {
        throw new Error(result.data.failed.length > 0 ? result.data.failed[0].reason : 'internal')
      }
      return result.data
    })
  },
  loadDeletedModels (context) {
    if (context.state.unsubscribeDeletedModels) {
      context.state.unsubscribeDeletedModels()
    }
    // Ad-Hoc models are for admins and scientists only, and firestore.rules refuses anyone else a
    // query that could return them. No orderBy: the table sorts, and with the filter it would need
    // a composite index.
    const authUser = context.rootGetters['auth/authUser'] || {}
    const all = db.collection('deleted_models')
    const visible = authUser.isAdmin || authUser.isScientist ? all : all.where('ad_hoc', '==', false)
    const unsubscribe = visible.onSnapshot(function (snapshot) {
      var deletedModels = []
      snapshot.forEach(doc => {
        deletedModels.push({
          ...doc.data(),
          id: doc.id
        })
      })
      context.commit('setDeletedModels', deletedModels)
    }, function (error) {
      console.error('Error loading deleted models: ', error)
    })
    context.commit('setUnsubscribeDeletedModels', unsubscribe)
  },
  stopDeletedModels (context) {
    if (context.state.unsubscribeDeletedModels) {
      context.state.unsubscribeDeletedModels()
      context.commit('setUnsubscribeDeletedModels', null)
    }
  },
  // payload: { trashId, newName (to restore under another name when the original is taken) }
  // Rejects with the reason in error.message: 'name-conflict', 'target-exists', 'storage-changed', ...
  restoreDeletedModel (context, payload) {
    const restoreModel = firebase.functions.httpsCallable('modelTrash-restoreModel')
    return restoreModel({ trashId: payload.trashId, newName: payload.newName || null }).then(result => result.data)
  },
  renameCustomModel (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(payload.client).update({
        name: payload.name,
        date_renamed: firebase.firestore.FieldValue.serverTimestamp()
      }).then(() => {
        resolve()
      }).catch(function (error) {
        console.error('Error renaming custom model: ', error)
        reject(error)
      })
    })
  },
  renameEkrmModel (context, payload) {
    return new Promise((resolve, reject) => {
      // Update category name in  the reference (only needed for more easily mapping the value to the Vuetify table)
      db.collection('ekrm').doc(payload.id).set({
        definition: {
          reference: {
            category: payload.name
          }
        },
        date_renamed: firebase.firestore.FieldValue.serverTimestamp()
      }, { merge: true }).then(() => {
        db.collection('ekrm').doc(payload.id).get().then(snapshot => {
          const data = snapshot.data()
          // Config is saved as a string in Firebase
          let config = JSON.parse(data.config)
          config.category = payload.name
          // Update category name in the config JSON (this is the one shown when creating a Custom Model)
          db.collection('ekrm').doc(payload.id).set({
            config: JSON.stringify(config) // Config is saved as a string in Firebase
          }, { merge: true }).then(() => {
            resolve()
          })
        })
      }).catch(function (error) {
        console.error('Error renaming EKRM model: ', error)
        reject(error)
      })
    })
  },
  setUserRolesWithAudit (context, payload) {
    return new Promise((resolve, reject) => {
      // Call the cloud function instead of direct DB access
      const setUserRolesWithAudit = firebase.functions.httpsCallable('permissions-setUserRolesWithAudit')
      setUserRolesWithAudit({
        userId: payload.userId,
        selectedRoles: payload.selectedRoles,
        userAgent: payload.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null)
      }).then((result) => {
        resolve(result.data)
      }).catch((error) => {
        console.error('Error updating user roles with audit:', error)
        reject(error)
      })
    })
  },
  getUserRoles (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('users').doc(payload.userId).collection('metadata').doc('roles').get().then(function (snapshot) {
        let roles = []
        if (typeof snapshot.data === 'function' && snapshot.data() && snapshot.data().roles) {
          roles = snapshot.data().roles
        }
        resolve(roles)
      })
    })
  },
  updateModelData (context, payload) {
    db.collection('clients').doc(payload.client).update({
      model_data: payload.modelData
    }).then(function (docRef) {
      console.log('Model data updated')
    }).catch(function (error) {
      console.error('Error updating model data', error)
    })
  },
  updateCustomModelConfig (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(payload.client).update({
        reference_model: payload.reference_model,
        date_config_updated: firebase.firestore.FieldValue.serverTimestamp()
      }).then(() => {
        resolve()
      }).catch(function (error) {
        console.error('Error updating custom model config: ', error)
        reject(error)
      })
    })
  },
  deleteUser (context, payload) {
    return new Promise((resolve, reject) => {
      // Call the secure Cloud Function instead of direct Firestore writes
      const deleteUserWithAudit = firebase.functions.httpsCallable('permissions-deleteUserWithAudit')

      deleteUserWithAudit({
        uid: payload.uid,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('User deleted with audit trail:', result.data)
        resolve(result.data)
      }).catch(error => {
        console.error('Error deleting user:', error)
        reject(error)
      })
    })
  },
  loadAuditLogs (context, payload) {
    return new Promise((resolve, reject) => {
      context.commit('setAuditLoading', true)

      const getAuditLogs = firebase.functions.httpsCallable('audit-getAuditLogs')

      getAuditLogs(payload).then((result) => {
        const logs = result.data.logs.map(log => ({
          ...log,
          timestamp: log.timestamp ? log.timestamp : null
        }))

        context.commit('setAuditLogs', {
          logs: logs,
          total: result.data.total || result.data.logs.length
        })

        resolve(result.data)
      }).catch(error => {
        console.error('Error loading audit logs:', error)

        // Check for Firestore index errors and provide helpful message
        if (error.code === 'failed-precondition' && error.message && error.message.includes('index')) {
          const indexError = new Error('Database index required for filtering. Please try filtering with fewer criteria or contact support.')
          indexError.isIndexError = true
          reject(indexError)
        } else {
          reject(error)
        }
      }).finally(() => {
        context.commit('setAuditLoading', false)
      })
    })
  },
  exportAuditLogs (context, payload) {
    return new Promise((resolve, reject) => {
      const exportAuditLogs = firebase.functions.httpsCallable('audit-exportAuditLogs')

      exportAuditLogs(payload).then((result) => {
        resolve(result.data)
      }).catch(error => {
        console.error('Error exporting audit logs:', error)

        // Check for Firestore index errors and provide helpful message
        if (error.code === 'failed-precondition' && error.message && error.message.includes('index')) {
          const indexError = new Error('Database index required for export filtering. Please try exporting with fewer filter criteria or contact support.')
          indexError.isIndexError = true
          reject(indexError)
        } else {
          reject(error)
        }
      })
    })
  }
}

// These collections hold thousands of large, read-only Firestore documents.
// Object.freeze() each element so Vue 2 skips deep reactivity conversion on them,
// which drastically cuts memory and GC pressure (and prevents the freeze/crash
// when these arrays are fed into v-data-tables). Freeze is shallow, which is all
// we need: nothing reassigns top-level fields of these objects in place.
const freezeEach = function (arr) {
  return Array.isArray(arr) ? arr.map(function (o) { return Object.freeze(o) }) : arr
}

const mutations = {
  setCustomModels (state, payload) {
    state.customModels = freezeEach(payload.customModels)
  },
  setMmmModels (state, payload) {
    state.mmmModels = freezeEach(payload.mmmModels)
  },
  setAllModels (state, payload) {
    state.allModels = freezeEach(payload.allModels)
  },
  setEkrmModels (state, payload) {
    // NOT frozen: manageEkrm.vue inline-edits item.definition.reference.category
    // directly via v-model, which requires these objects to stay reactive/mutable.
    state.ekrmModels = payload.ekrmModels
  },
  setEkrmModelsObj (state, payload) {
    state.ekrmModelsObj = payload.ekrmModelsObj
  },
  setReferenceModels (state, payload) {
    state.referenceModels = payload.referenceModels
  },
  setReferenceModelsArray (state, payload) {
    state.referenceModelsArray = freezeEach(payload.referenceModelsArray)
  },
  setManagedUsers (state, payload) {
    state.managedUsers = freezeEach(payload.users)
  },
  setUserList (state, payload) {
    state.userList = payload.users
  },
  setGroups (state, payload) {
    state.groups = payload.groups
  },
  setAuditLogs (state, payload) {
    state.auditLogs = payload.logs
    state.auditTotal = payload.total
  },
  setAuditLoading (state, loading) {
    state.auditLoading = loading
  },
  setUnsubscribeGroups (state, unsubscribe) {
    state.unsubscribeGroups = unsubscribe
  },
  setUnsubscribeManagedUsers (state, unsubscribe) {
    state.unsubscribeManagedUsers = unsubscribe
  },
  setUnsubscribeEkrmModels (state, unsubscribe) {
    state.unsubscribeEkrmModels = unsubscribe
  },
  setUnsubscribeCustomModels (state, unsubscribe) {
    state.unsubscribeCustomModels = unsubscribe
  },
  setUnsubscribeMmmModels (state, unsubscribe) {
    state.unsubscribeMmmModels = unsubscribe
  },
  setUnsubscribeAllModels (state, unsubscribe) {
    state.unsubscribeAllModels = unsubscribe
  },
  setDeletedModels (state, deletedModels) {
    state.deletedModels = freezeEach(deletedModels)
  },
  setUnsubscribeDeletedModels (state, unsubscribe) {
    state.unsubscribeDeletedModels = unsubscribe
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
